import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function CourseDetail() {
  const { id }            = useParams();
  const { isSupervisor }  = useAuth();
  const [course, setCourse]   = useState(null);
  const [weeks, setWeeks]     = useState([]);
  const [openWeek, setOpenWeek] = useState(null);
  const [loading, setLoading]   = useState(true);

  const load = () => {
    Promise.all([
      api.get(`/courses/${id}`),
      api.get(`/courses/${id}/structure`),
    ]).then(([cr, sr]) => {
      setCourse(cr.data.data);
      const ws = sr.data.data.weeks;
      setWeeks(ws);
      setOpenWeek(ws[0]?.id ?? null);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  const addWeek = async () => {
    await api.post(`/courses/${id}/weeks`, {});
    load();
  };

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary" /></div>;
  if (!course)  return <div className="text-center py-5 text-danger">Course not found</div>;

  const totalDays     = weeks.reduce((s, w) => s + w.days.length, 0);
  const completedDays = weeks.reduce((s, w) => s + w.days.filter(d => d.progress?.completed).length, 0);
  const pct           = totalDays > 0 ? Math.round(completedDays / totalDays * 100) : 0;

  return (
    <>
      {/* Header */}
      <div className="content-section mb-4" style={{background:'linear-gradient(135deg,#0d6efd,#0dcaf0)',color:'#fff'}}>
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-2">
          <div>
            <h4 className="fw-bold mb-1">{course.title}</h4>
            <p className="mb-0 opacity-75">{course.description || 'No description provided'}</p>
          </div>
          {!isSupervisor && (
            <div className="text-end">
              <div className="fs-3 fw-bold">{pct}%</div>
              <div className="opacity-75 small">{completedDays} / {totalDays} days</div>
            </div>
          )}
        </div>
        {!isSupervisor && (
          <div className="progress mt-3" style={{height:8,background:'rgba(255,255,255,.3)'}}>
            <div className="progress-bar bg-white" style={{width:`${pct}%`}} />
          </div>
        )}
      </div>

      {/* Week list */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="fw-bold mb-0">Course Content</h6>
        {isSupervisor && (
          <button className="btn btn-primary btn-sm" onClick={addWeek}>
            <i className="bi bi-plus-lg me-1" />Add Week
          </button>
        )}
      </div>

      {weeks.map(week => (
        <div key={week.id} className="mb-3">
          <div className="week-header" onClick={() => setOpenWeek(openWeek === week.id ? null : week.id)}>
            <div className="d-flex align-items-center gap-2">
              <span className="badge bg-primary-subtle text-primary">Week {week.week_no}</span>
              <span className="fw-semibold">{week.title || `Week ${week.week_no}`}</span>
              {!isSupervisor && (
                <span className="text-muted small">
                  ({week.days.filter(d => d.progress?.completed).length}/{week.days.length} done)
                </span>
              )}
            </div>
            <i className={`bi bi-chevron-${openWeek === week.id ? 'up' : 'down'} text-muted`} />
          </div>

          {openWeek === week.id && (
            <div className="ps-2">
              {week.days.map(day => (
                <DayItem key={day.id} day={day} week={week} courseId={id} isSupervisor={isSupervisor} />
              ))}
            </div>
          )}
        </div>
      ))}
    </>
  );
}

function DayItem({ day, week, courseId, isSupervisor }) {
  const locked    = !isSupervisor && day.is_locked;
  const completed = day.progress?.completed;

  const icon = completed ? 'bi-check-circle-fill text-success'
    : locked ? 'bi-lock-fill text-muted'
    : 'bi-play-circle-fill text-primary';

  return (
    <div className={`day-item ${completed ? 'completed' : ''} ${locked ? 'locked' : ''}`}>
      <i className={`bi ${icon} day-icon`} />
      <div className="flex-grow-1">
        <div className="fw-semibold small">{day.title || `Day ${day.day_no}`}</div>
        <div className="text-muted" style={{fontSize:'.75rem'}}>
          {completed ? 'Completed' : locked ? 'Locked — complete previous day first' : 'Available'}
        </div>
      </div>
      {!locked && (
        isSupervisor ? (
          <Link to={`/course-builder/${courseId}`} state={{ dayId: day.id }} className="btn btn-outline-secondary btn-sm">
            <i className="bi bi-pencil" />
          </Link>
        ) : (
          <Link to={`/courses/${courseId}/week/${week.week_no}/day/${day.day_no}`} className="btn btn-primary btn-sm">
            {completed ? 'Review' : 'Start'}
          </Link>
        )
      )}
    </div>
  );
}
